import { Checkbox } from "@usitsdasdesign/dds-react";
import Input from "atoms/Input";
import Text from "atoms/Text";
import { debounce } from "helper/util";
import React, { useState, useEffect, useRef, useCallback } from "react";
import SelectionPill from "./Select/SelectionPill";
import useAsyncOptions from "hooks/useAsyncOptions";

const CustomAsyncSelect = ({
    loadOptions,
    isMulti = true,
    placeholder = "Search...",
    value = isMulti ? [] : null,
    setValue,
    onChange = () => false,
    name,
    options: defaultOptions = [],
    watch
}) => {
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [inputValue, setInputValue] = useState("");
    const [visibleOptions, setVisibleOptions] = useState([]);
    const dropdownRef = useRef();
    const hasDefaultOptions = Array.isArray(defaultOptions) && defaultOptions.length > 0;

    const {
        options,
        loading,
        fetchOptions,
        initialFetch
    } = useAsyncOptions(loadOptions, watch);

    useEffect(() => {
        if (!hasDefaultOptions) {
            initialFetch();
        }
    }, [initialFetch, hasDefaultOptions]);

    useEffect(() => {
        if (!hasDefaultOptions && options.length > 0) {
            setVisibleOptions(options);
        }
    }, [options, hasDefaultOptions]);

    const debouncedFetch = useCallback(
        debounce((searchValue) => {
            fetchOptions(searchValue);
        }, 500),
        [fetchOptions]
    );

    const handleInputChange = (e) => {
        setInputValue(e);
        debouncedFetch(e);
    };

    const handleOptionSelect = (option) => {
        if (isMulti) {
            const newValue = value.some((item) => item.value === option.value)
                ? value.filter((item) => item.value !== option.value)
                : [...value, option];
            setValue(name, newValue);
            onChange(newValue);
        } else {
            setValue(name, option);
            onChange(option);
            setInputValue("");
        }
    };

    const handleRemoveOption = (optionToRemove) => {
        const newValue = value.filter(item => item.value !== optionToRemove.value);
        setValue(name, newValue);
        onChange(newValue);
    };

    const isSelected = (option) =>
        isMulti
            ? value.some((item) => item.value === option.value)
            : value?.value === option.value;

    const handleKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setFocusedIndex((prev) => Math.min(prev + 1, visibleOptions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setFocusedIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === "Enter" && focusedIndex >= 0) {
            e.preventDefault();
            handleOptionSelect(visibleOptions[focusedIndex]);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setFocusedIndex(-1);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (inputValue !== '') {
            const optionsToFilter = hasDefaultOptions ? defaultOptions : options;
            const filtered = optionsToFilter?.length  ? optionsToFilter?.filter(option =>
                option?.label?.toLowerCase().includes(inputValue.toLowerCase())
            ): [];
            setVisibleOptions(filtered);
        } else {
            setVisibleOptions(hasDefaultOptions ? defaultOptions : options);
        }
    }, [inputValue, options, defaultOptions, hasDefaultOptions]);

    const handleSelectAll = () => {
        const unselectedVisibleOptions = visibleOptions.filter(
            option => !value.some(item => item.value === option.value)
        );
        if (unselectedVisibleOptions.length === 0) {
            const newValue = value.filter(
                item => !visibleOptions.some(visibleOpt => visibleOpt.value === item.value)
            );
            setValue(name, newValue);
            onChange(newValue);
        } else {
            const newValue = [...value, ...unselectedVisibleOptions];
            setValue(name, newValue);
            onChange(newValue);
        }
    };

    const areAllVisibleOptionsSelected = () => {
        return visibleOptions.length > 0 &&
            visibleOptions.every(option =>
                value.some(item => item.value === option.value)
            );
    };

    const RenderOption = (option, index) => {
        return (
            <div
                key={option.value + index}
                title={option.label}
                className={`text-sm truncate flex gap-1.5 items-center py-1 px-1.5 cursor-pointer transition-all ${focusedIndex === index ? "bg-neutral-200" : "hover:bg-neutral-100"
                    }`}
                onClick={() => {
                    if (option.value === '*') {
                        handleSelectAll();
                    } else {
                        handleOptionSelect(option);
                    }
                }}
            >
                <Checkbox
                    value={option.value === '*' ? areAllVisibleOptionsSelected() : isSelected(option)}
                    color="dark"
                    className="pointer-events-none"
                />
                <label className="truncate flex-1 capitalize cursor-pointer">
                    {option.label}
                </label>
            </div>
        );
    };
    return (
        <div
            className="relative h-full outline-none"
            ref={dropdownRef}
            onKeyDown={handleKeyDown}
            tabIndex={0}
        >
            <div className="flex flex-col h-full">
                <div className="border border-gray-200 py-1.5 px-2 flex flex-col gap-2 max-h-20 overflow-auto">
                    {(value && value.length) ? (
                        <div className="flex flex-wrap gap-1">
                            {isMulti ? (
                                value.map((option) => (
                                    <SelectionPill
                                        key={option.value}
                                        label={option.label}
                                        onRemove={() => handleRemoveOption(option)}
                                    />
                                ))
                            ) : (
                                value && (
                                    <SelectionPill
                                        label={value.label}
                                        onRemove={() => {
                                            setValue(name, null);
                                            onChange(null);
                                        }}
                                    />
                                )
                            )}
                        </div>
                    ) : null}
                    <Input
                        type="text"
                        value={inputValue}
                        valueChanged={handleInputChange}
                        placeholder={placeholder}
                        autoFocus
                        size="m"
                        inputClass="filter-input border-none !p-0"
                    />
                </div>
                <div className="flex-1 overflow-auto my-1">
                    {loading ? (
                        <Text className="flex justify-center items-center flex-col text-gray-1100 h-full">
                            Loading.....
                        </Text>
                    ) : visibleOptions.length ? (
                        <>
                            {RenderOption(
                                { label: 'Select All', value: '*' },
                                0
                            )}
                            {visibleOptions.map((option, index) => (
                                RenderOption(option, index + 1)
                            ))}
                        </>
                    ) : (
                        <Text className="flex justify-center items-center flex-col h-full text-gray-1100">
                            No options found
                        </Text>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CustomAsyncSelect;